import React, { useEffect, useMemo, useRef, useState, useContext, useCallback } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { batch, useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import queryString from 'query-string';

/* constants */
import {
  FormTypes,
  ModalTypes,
  dispatchTypes,
  APP_ROUTES,
  ORIGINS,
  LEAVE_WARNING_MSG,
  FEET_TO_METERS,
} from 'apps/web_forms/utils/constants';

/* api */
import {
  deleteDraft,
  isPublicForm,
  submitImages,
  submitForm,
  submitDraft,
  getAllDrafts,
  deleteDrafts
} from 'apps/web_forms/api/api';

/* hooks */
import useXForm from 'apps/web_forms/hooks/useXForm';
import useLoadFormResources from 'apps/web_forms/hooks/useLoadFormResources';
import locationHook from 'apps/web_forms/hooks/useLocation';
// ---- Added custom hook for exit modal logic ----
import { useExitPageModal } from 'apps/web_forms/hooks/useExitPageModal';

/* slices */
import { setModal, resetModal, setSelectedForm, showActionSuccessMessage } from 'apps/web_forms/slices/appSlice';
import { setIsSubmitting, resetFormSliceState, updateFieldsResourceUpdateHighlight, setResourceVersions, } from 'apps/web_forms/slices/formSlice';
import { resetMultimediaSliceState } from 'apps/web_forms/slices/multimediaSlice';
import { resetResourceSliceState } from 'apps/web_forms/slices/resourceSlice';

/* state */
import {
  selectSelectedForm,
  selectIsLoading,
  selectDraftList,
  selectFieldErrors,
  selectRequiredResourcesLoading,
  selectIsSubmitting,
  selectAreProcessesRunning,
  selectRepeatGroupFields
} from 'apps/web_forms/hooks/useGlobalSelector';

/* utils */
import {
  buildSubmittedTree,
  parseXml,
  filterEnterKey,
  getFieldsAffectedByUpdate
} from 'apps/web_forms/components/Form/utils';
import {
  locationParse,
  isWithinRadius,
} from 'apps/web_forms/utils/locationHelpers';
import { getMeasureUnitFromString } from 'apps/web_forms/utils/inputsUtils';
import { MultiDispatchError } from 'apps/web_forms/utils/errors';
import findSelectedForm from 'apps/web_forms/utils/findSelectedForm';

/* components */
import { NotificationBarContext } from 'apps/web_forms/contexts/NotificationBarContext';
import { StyledForm } from 'apps/web_forms/components/Form/styles';
import { ModalWrapper } from 'apps/web_forms/components/Modals';
import Loading from 'apps/web_forms/components/commons/Loading';
import Notification from 'apps/web_forms/components/Notification'
import Form from 'apps/web_forms/components/Form';
import FormOld from 'apps/web_forms/components/Form/FormOld';
import Draft from 'apps/web_forms/components/Draft/Draft';
import Dispatch from 'apps/web_forms/components/Dispatch/Dispatch';
import Submission from 'apps/web_forms/components/Submission/Submission';

const TypeMap = {
  [FormTypes.FORM]: Form,
  [FormTypes.DRAFT]: Draft,
  [FormTypes.DISPATCH]: Dispatch,
  [FormTypes.DISPATCH_DRAFT]: Draft,
  [FormTypes.SUBMISSION]: Submission,
  [FormTypes.PUBLIC_FORM]: Form,
};

const FormWrapper = () => {
  const formMethods = useForm();
  const formRef = useRef(null);
  const { search } = useLocation();
  const { getUserAccuracy } = locationHook();
  const [notificationBox, setNotificationBox] = useState(null);
  const {
    setShowNotificationBar,
    setNotificationBarText,
    setNotificationBarType,
    setAlignLeft,
    setButtons,
    setRightContents,
    showNotificationBar,
  } = useContext(NotificationBarContext);

  const params = queryString.parse(search);
  const { id: authenticatedFormId, submissionId: submissionId } = useParams();
  const id = !isPublicForm() ? authenticatedFormId : window.sharedFormFormID;
  const isDraft = params.origin === ORIGINS.Draft;
  const draftId = isDraft ? params.id : undefined;
  
  const fieldErrors = useSelector(selectFieldErrors);
  const requiredResourcesLoading = useSelector(selectRequiredResourcesLoading);
  const isSubmitting = useSelector(selectIsSubmitting);
  const areProcessesRunning = useSelector(selectAreProcessesRunning);
  const isLoading = useSelector(selectIsLoading);
  const repeatGroupFields = useSelector(selectRepeatGroupFields);
  const selectedForm = useSelector(selectSelectedForm);

  //Needed for submit
  let { data: stateDrafts } = useSelector(selectDraftList) || [];
  let [drafts, setDrafts] = useState(stateDrafts);

  // Drafts unfortunately are needed to be populated all the time because:
  // 1. They are needed to delete the drafts of dispatches that have been submitted
  // 2. They are needed to delete other drafts if they belong to the same form as the one being submitted (dispatch drafts)
  // 3. They are needed to update draft resources
  useEffect(() => {
    async function fetchDrafts() {
      drafts = await getAllDrafts();
      setDrafts(drafts);
    }
    fetchDrafts();

    return () => {
      setDrafts([]);
    }
  }, []);

  const selectedFormType = selectedForm?.type;

  const draftMetadata = useMemo(
    () => drafts?.find((draft) => draft.identifier === draftId),
    [drafts]
  );
  const isDispatchDraft = selectedForm?.type === FormTypes.DISPATCH_DRAFT;
  const isDispatch = window.location.href.includes('origin=dispatch');

  const dispatch = useDispatch();
  const history = useHistory();

  const { form, error, loading } = useXForm({
    formVersion: selectedForm?.version,
    formId: id,
    submissionId: submissionId,
  });

  const {
    getResourceVersionsToSaveDraft,
    anyNeedUpdate,
    resourceUpdatesNeeded,
  } = useLoadFormResources(form, isDraft, draftMetadata, loading);

  const updateResources = useCallback(() => {
    // Move some or all of this to useLoadFormResources?
    const newResourceVersions = {};
    resourceUpdatesNeeded.forEach((versions, resourceIdentifierHash) => {
      const { latestVersion } = versions;
      newResourceVersions[resourceIdentifierHash] = latestVersion;
    });
    dispatch(setResourceVersions(newResourceVersions));

    const updatedResources = Array.from(resourceUpdatesNeeded.keys());
    const fieldsAffectedByUpdate = getFieldsAffectedByUpdate(
      updatedResources,
      form.inputList,
      repeatGroupFields || {}
    );

    dispatch(
      updateFieldsResourceUpdateHighlight({
        fieldsAffectedByUpdate,
        isHighlighted: true,
      })
    );
    dispatch(resetModal());
  }, [resourceUpdatesNeeded, form.inputList, repeatGroupFields]);

  useEffect(() => {
    if (error.error) {
      cleanup();
      history.push(APP_ROUTES.getErrorPageByCode(error.code));
    }
  }, [error]);

  useEffect(
    function setResourceUpdateNotification() {
      if (anyNeedUpdate) {
        const updatedResources = Array.from(resourceUpdatesNeeded.keys());
        const fieldsAffectedByUpdate = getFieldsAffectedByUpdate(
          updatedResources,
          form.inputList,
          repeatGroupFields || {}
        );

        setNotificationBox({
          text: fieldsAffectedByUpdate.length > 1 ? 'New Resources Updates Available' : 'New Resource Update Available',
          type: 'warning',
          style: '2-columns',
          children: <button className='button-adjustment filled-button' onClick={() => {
            dispatch(setModal({
              type: ModalTypes.RESOURCE_UPDATE_CONFIRMATION_MODAL,
              extraProps: {
                setUpdateResourceConfirmationOpen: () => {
                  dispatch(resetModal());
                },
                numAffectedFields: fieldsAffectedByUpdate.length,
                updateResources: updateResources,
              }
            }));
          }}>{fieldsAffectedByUpdate.length > 1 ? 'Update Resources' : 'Update Resource'}</button>,
        });
      }
    },
    [anyNeedUpdate, resourceUpdatesNeeded, repeatGroupFields]
  );

  const imagesToSubmit = useSelector((state) => state.multimedia.imagesToSubmit);
  const imageDraftValues = useSelector((state) => state.multimedia.imageDraftValues);
  const rootLevelFields = useSelector((state) => state.form.rootLevelFields);
  
  useEffect(() => {
    async function findFormOnReload() {
      const { foundSelectedForm, forms } = isDraft
        ? await findSelectedForm(draftId, params)
        : await findSelectedForm(id, params);

      dispatch(setSelectedForm(foundSelectedForm));
    }

    if (!selectedForm?.identifier) {
      findFormOnReload();
    }

    return () => {
      // Clean state related to the form when the user leaves it
      dispatch(resetFormSliceState());
      dispatch(resetMultimediaSliceState());
      dispatch(resetResourceSliceState())

      // If this is false, a Redux middleware will block all actions trying to modify the form state
      dispatch(setSelectedForm({}));
    };
  }, []);

  useEffect(() => {
    // Enable confirm request when the user tries to close the tab or window. Not applied in development environment
    const beforeUnloadHandler = (e) => {
      e.returnValue = LEAVE_WARNING_MSG; // eslint-disable-line no-param-reassign
      return LEAVE_WARNING_MSG;
    };

   
    window.addEventListener('beforeunload', beforeUnloadHandler);

    return () => {
      if (beforeUnloadHandler) {
        // Remove confirm request when user closes the tab or window
        window.removeEventListener('beforeunload', beforeUnloadHandler);
      }
    };
  }, []);

  const clearNotificationBar = () => {
    // clear notification bar
    setShowNotificationBar(false);
    setAlignLeft(true);
    setNotificationBarText('');
    setNotificationBarType('info');
    setButtons([]);
    setRightContents(undefined);
  };

  // For cleaning any global (i.e. redux) state that needs it before leaving form page
  const cleanup = () => {
    clearNotificationBar();
  };

  const goBackUrl = useMemo(() => {
    // Should we use TYPE from selectedForm or from the params? (One less hook to use)
    switch (params.origin) {
      case ORIGINS.Dispatch:
        return APP_ROUTES.dispatches;
      case ORIGINS.Submission:
        return APP_ROUTES.webstorePage(id);
      case ORIGINS.Geolocated:
      case ORIGINS.Geofenced:
        return APP_ROUTES.getDispatchesDetails(params.origin);
      case ORIGINS.Draft:
        return APP_ROUTES.draftsPage;
      default:
        return APP_ROUTES.mainPage;
    }
  }, [search]);

  const FormComponent = useMemo(() => {
    if (Object.keys(selectedForm).length > 0) {
      if (window?.sharedFormFormID) {
        return Form;
      }

      if(window.location.href.includes('submission')){
        return TypeMap[FormTypes.SUBMISSION]; // Since there is no submission API, we have to hardcode it.
      }

      if (!selectedFormType) {
        return FormOld;
      }
      return TypeMap[selectedFormType] || FormOld;
    }
    return null;
  }, [selectedFormType, selectedForm]);

  const submit = async(type = 'form', description = null, action = 'submit') =>  {
    const submitData = formMethods.getValues();

    Object.entries(submitData).forEach(([key, value]) => {
      if (value === undefined || value === null) submitData[key] = '';
    });

    if (Object.keys(imageDraftValues).length > 0) {
      Object.keys(imageDraftValues).forEach((fieldId) => {
        const imageProperties = {
          type: 'image',
          imageIdentifier: imageDraftValues[fieldId].imageIdentifier,
        };
        submitData[fieldId] = imageProperties;
      });
    }

    if (Object.keys(imagesToSubmit).length > 0) {
      const imagesUUIDValues = await submitImages(imagesToSubmit);
      if (imagesUUIDValues.error) {
        setNotificationBox({
          text: 'There was a problem during the image uploading',
          type: 'error',
        });
        setTimeout(() => {
          setNotificationBox(null);
        }, 3000);

        return null;
      }
      Object.keys(imagesUUIDValues).forEach((fieldId) => {
        const imageProperties = {
          type: 'image',
          imageIdentifier: imagesUUIDValues[fieldId],
        };
        submitData[fieldId] = imageProperties;
      });
    }

    if (type === 'form') {
      // DME-2913 remove values from inactive fields from submitData
      for (const [key, value] of Object.entries(rootLevelFields)) {
        const { isActive } = value;
        if (!isActive) {
          delete submitData[key];
        }
      }
    }

    const submittedTree = buildSubmittedTree(submitData);
    const finalXMLString = parseXml(submittedTree);
    const isUpdateExisting = type === 'draft' && isDraft;

    try {
      const formOptions = {
        finalXMLString,
        formVersion: selectedForm.version,
        formNamespace: form.namespace,
        submissionId: submissionId,
      };
      const draftOptions = {
        ...formOptions,
        description,
        isUpdateExisting,
        draftId: isUpdateExisting && draftId,
        resourceVersions: getResourceVersionsToSaveDraft(),
      };
      const response =
        type === 'form'
          ? await submitForm(formOptions)
          : await submitDraft(draftOptions);

      if (response?.status >= 200 && response?.status < 300) {
        await onSuccesfulSubmit(type);
        // ---- Custom hook integration: unblock navigation on successful submission ----
        if (unblockRef.current) {
          unblockRef.current();
        }
        // onSuccesfulSubmit errors if any of its http calls fail, so if we get here everything succeeded
        if (!isPublicForm()) {
          cleanup();
          if (params.origin === ORIGINS.Submission) {
            history.push(APP_ROUTES.submissionSuccessPage); // push the blank success page onto the history to clear the form for the redirect
            window.location.replace(goBackUrl);
          } else {
            history.push(goBackUrl);
          }
        }
        dispatch(showActionSuccessMessage(true));
      }
    } catch (e) {
      if (e instanceof MultiDispatchError) {
        dispatch(setModal({ 
          type: ModalTypes.MULTI_DISPATCH_FAIL_MODAL,
          extraProps: { 
            setMultiDispatchFailModalOpen: () => {
              dispatch(resetModal());
            },
            onClick: () => {
              dispatch(resetModal());
              cleanup();
              history.push(goBackUrl);
            }
          }
        }));
      } else {
        // TODO: Add bugsnag
        console.log('There was an error submitting or saving the form', e);
        cleanup();
        history.push(APP_ROUTES.getErrorPageByCode(500));
      }
    } finally {
      dispatch(setIsSubmitting(false));
    }
    return true;
  }

  // Upon successful completion of the submission itself
  const onSuccesfulSubmit = async (type) => {
    const savingDraft = type === 'draft';
    if (!savingDraft) {
      // It was an actual form submission (whether from a normal form, dispatch, or draft), not saving/updating a draft
      if (isDraft) {
        // If we submitted a draft, delete it
        try {
          await deleteDraft(draftId);
          drafts = drafts.filter((draft) => draft.form_id !== parseInt(id) && draft.id === parseInt(draftId));
        } catch (e) {
          if (isDispatchDraft) {
            console.error(e);
            console.log(`you can't submit a dispatch draft twice`);
            dispatch(setModal({ 
              type: ModalTypes.MULTI_DISPATCH_FAIL_MODAL,
              extraProps: { 
                setMultiDispatchFailModalOpen: () => {
                  dispatch(resetModal());
                },
                onClick: () => {
                  dispatch(resetModal());
                  cleanup();
                  history.push(goBackUrl);
                }
              }
            }));
          } else {
            throw e;
          }
        }
        if (isDispatchDraft) {
          const draftsOfDispatch = drafts.filter(
            (otherDraft) =>
              otherDraft.form_id === parseInt(id) && otherDraft.id !== parseInt(draftId)
          );
          await deleteDrafts(draftsOfDispatch);
        }
      }

      if (isDispatch) {
        const draftsOfDispatch = drafts.filter((draft) => draft.form_id === parseInt(id));
        await deleteDrafts(draftsOfDispatch);
      }
    }
  };

  const canSendGeoFencedForm = async () => {
    let allowSendForm = true;
    if (form.formType === dispatchTypes.GEO_FENCED) {
      try {
        const { latitude, longitude } = await getUserAccuracy();
        const radius = getMeasureUnitFromString(form?.geoFenceRadius);
        allowSendForm = isWithinRadius(
          { latitude, longitude },
          locationParse(form.location),
          radius.unit === 'ft' ? radius.value / FEET_TO_METERS : radius.value
        );

        if (!allowSendForm) {
          batch(() => {
            dispatch(
              setModal({
                type: ModalTypes.RANGE_FAILURE_MODAL,
                extraProps: { radius },
              })
            );
            dispatch(setIsSubmitting(false));
          });
        }
      } catch (e) {
        batch(() => {
          dispatch(setModal({ type: ModalTypes.LOCATION_FAILURE_MODAL }));
          dispatch(setIsSubmitting(false));
        });
        allowSendForm = false;
      }
    }
    return allowSendForm;
  };

  // Upon beginning to submit a valid form(/dispatch/draft)
  const onValidSubmit = () => {
    if (requiredResourcesLoading) {
      dispatch(setModal({ type: ModalTypes.RESOURCE_LOADING_MODAL }));
      return;
    }

    if (!formRef.current) return;
    if (!formRef.current.checkValidity()) return;
    dispatch(setIsSubmitting(true));
  };

  const handleSubmit = () => {
    if (Object.keys(fieldErrors).length) {
      dispatch(
        setModal({
          type: ModalTypes.SUBMIT_FAIL_MODAL,
          extraProps: {
            fieldErrors: fieldErrors,
          }
        })
      );
    } else {
      onValidSubmit();
    }
  };

  // Submits the form if there aren't any required resource loading or any running process
  useEffect(() => {
    async function onSubmitting() {
      if (isSubmitting) {
        if (!areProcessesRunning) {
          if (await canSendGeoFencedForm()) {
            submit();
          }
        }
      }
    }
    onSubmitting();
  }, [isSubmitting, areProcessesRunning]);

  // ---- Manage blocking navigation using the custom exit modal hook ----
  const shouldBlock = formMethods.formState.isDirty;
  const { unblockRef } = useExitPageModal(shouldBlock);

  return (
    <>
      {(loading || isLoading || isSubmitting) && <Loading />}
      {FormComponent && (
        <>
          <ModalWrapper />
          {notificationBox && (
            <Notification
              notificationText={notificationBox.text}
              type={notificationBox.type}
              style={notificationBox.style}
              children={notificationBox.children}
            />
          )}
          <FormProvider {...formMethods}>
            <StyledForm
              hide={isLoading || loading}
              className={`form-container`}
              onKeyDown={filterEnterKey}
              onSubmit={formMethods.handleSubmit(handleSubmit, handleSubmit)}
              ref={formRef}
              data-testid='form'
            >
              <FormComponent
                filterEnterKey={filterEnterKey}
                form={form}
                goBackUrl={goBackUrl}
                selectedForm={selectedForm}
                submit={submit}
                cleanup={cleanup}
                submissionId={submissionId}
                loading={loading}
                isSubmitting={isSubmitting}
                showNotificationBar={showNotificationBar}
              />
            </StyledForm>
          </FormProvider>
        </>
      )}
    </>
  );
};

export default FormWrapper;
